import { throttle } from 'lodash-es';
import type { FC } from 'react';
import { useEffect, useRef } from 'react';
import styled from 'styled-components';

import { Carousel } from '@xing-com/carousel';
import { HeroCard } from '@xing-com/crate-components-hero-card';
import { useNetworkTracking } from '@xing-com/crate-network-tracking';
import { spaceL } from '@xing-com/tokens';

import type { GetNetworkTopRecruitersQuery } from '../../graphql/get-network-top-recruiters.gql-types';

type HeroCarouselProps = {
  items: NonNullable<
    GetNetworkTopRecruitersQuery['networkTopRecruiters']
  >['collection'];
};

const StyledHeroCard = styled(HeroCard)`
  margin: 0;
`;

export const HeroCarousel: FC<HeroCarouselProps> = ({ items }) => {
  const { trackNetworkNWTExtended } = useNetworkTracking();
  const containerRef = useRef<HTMLDivElement>(null);

  const profileLinkTracking =
    '?sc_o=contacts_network_hpreco_profile_click&sc_o_PropActionOrigin=contacts_network_hpreco_module_reason_spotlight';

  const handleResizeHelper = (
    container: HTMLElement,
    carousel: HTMLElement
  ): void => {
    const marginLeft = container.getBoundingClientRect().left;

    carousel.style.marginRight = `-${marginLeft}px`;
    carousel.style.marginLeft = `-${marginLeft}px`;
    carousel.style.paddingLeft = `${marginLeft}px`;

    // oxlint-disable-next-line typescript/consistent-type-assertions
    const carouselLastChild = carousel.lastChild as HTMLElement;

    if (carouselLastChild) {
      carouselLastChild.style.marginRight = `0px`;

      const existingSpacing = container?.querySelector<HTMLElement>(
        '[data-spacing="true"]'
      );

      const spacing = existingSpacing
        ? existingSpacing
        : document.createElement('div');
      spacing.style.minWidth = `${marginLeft}px`;
      spacing.style.height = '1px';
      spacing.dataset.spacing = 'true';

      if (!existingSpacing) {
        carousel.append(spacing);
      }
    }
  };

  useEffect(() => {
    const container = containerRef.current;

    const carousel = container?.querySelector<HTMLElement>(
      '[data-xds="Carousel"]'
    );

    if (!container || !carousel) return;

    const handleResize = throttle(
      () => handleResizeHelper(container, carousel),
      100
    );

    window.addEventListener('resize', handleResize, false);

    setTimeout(handleResize, 100);

    return () => window.removeEventListener('resize', handleResize);
  }, [containerRef]);

  const onClickCardTrack = (position: number): void => {
    trackNetworkNWTExtended({
      event: 'opened',
      element: 'hpreco_profile',
      elementDetail: 'hpreco_module_reason_spotlight',
      application: 'wbm',
      moduleTitle: 'hiring_professional_spotlight',
      positionInModule: position,
      page: 'network/network',
    });
  };

  return (
    <div ref={containerRef}>
      <Carousel
        hideScrollbar
        itemWidth={'240px'}
        itemMinWidth={'240px'}
        itemMaxWidth={'240px'}
        // @ts-expect-error FIXME: SC6
        itemHeight={'320px'}
        itemMinHeight={'320px'}
        itemMaxHeight={'320px'}
        itemSpacing={spaceL}
        itemSpacingConfined={spaceL}
        itemSpacingWide={spaceL}
        itemSnapPosition="center"
      >
        {items?.map((recruiter, index) => {
          const primaryJob = recruiter?.profileWorkExperience?.collection?.find(
            (element) => element?.primaryJob
          );

          return (
            <StyledHeroCard
              key={recruiter?.id}
              name={recruiter?.xingId?.displayName || ''}
              jobTitle={primaryJob?.jobTitle || ''}
              companyName={primaryJob?.company?.companyName || ''}
              profileImage={recruiter?.xingId?.profileImage?.[0]?.url || ''}
              profileLink={`/profile/${
                recruiter?.xingId?.pageName || ''
              }${profileLinkTracking}`}
              logoImage={primaryJob?.company?.logos?.logo32px || ''}
              logoLink={primaryJob?.company?.links?.public || ''}
              highlightInfo={
                recruiter?.hiringProfile?.hiringSpotlightSummary?.value || ''
              }
              onClick={() => onClickCardTrack(index)}
            />
          );
        })}
      </Carousel>
    </div>
  );
};
